/**
 * Convert GitHub-Flavored Markdown pipe tables into HTML <table> fragments.
 *
 * Exam UIs inject stems as HTML (dangerouslySetInnerHTML). Curated papers already
 * store real <table> markup; some generated/DB stems still use GFM pipes, which
 * otherwise render as literal "| … |" text. This keeps the HTML-only contract and
 * only rewrites recognizable pipe tables.
 */

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function splitCells(line) {
  let s = String(line).trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((cell) => cell.trim());
}

function isSeparatorRow(line) {
  const cells = splitCells(line);
  if (cells.length < 2) return false;
  return cells.every((cell) => /^:?-{1,}:?$/.test(cell));
}

function isPipeRow(line) {
  const trimmed = String(line).trim();
  if (!trimmed.includes('|')) return false;
  return splitCells(trimmed).length >= 2;
}

function alignFromSep(cell) {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
}

function cellHtml(content, align) {
  const style = align ? ` style="text-align:${align}"` : '';
  return `${style}>${escapeHtml(content)}`;
}

function tableToHtml(headerLine, separatorLine, bodyLines) {
  const headers = splitCells(headerLine);
  const seps = splitCells(separatorLine);
  const aligns = headers.map((_, i) => alignFromSep(seps[i] || '---'));

  const ths = headers
    .map((h, i) => `<th${cellHtml(h, aligns[i])}</th>`)
    .join('');

  const body = bodyLines
    .map((line) => {
      const cells = splitCells(line);
      const tds = headers
        .map((_, i) => `<td${cellHtml(cells[i] ?? '', aligns[i])}</td>`)
        .join('');
      return `<tr>${tds}</tr>`;
    })
    .join('');

  return `<table><thead><tr>${ths}</tr></thead><tbody>${body}</tbody></table>`;
}

/**
 * Split HTML/text into lines on newlines or <br>, remembering each separator
 * so non-table regions keep their original breaks.
 */
function splitLines(input) {
  const lines = [];
  const separators = [];
  let last = 0;
  const re = /(?:\r\n|\n|<br\s*\/?>)/gi;
  let match;
  while ((match = re.exec(input)) !== null) {
    lines.push(input.slice(last, match.index));
    separators.push(match[0]);
    last = match.index + match[0].length;
  }
  lines.push(input.slice(last));
  return { lines, separators };
}

function joinLines(lines, separators) {
  let out = lines[0] ?? '';
  for (let i = 0; i < separators.length; i += 1) {
    out += separators[i] + (lines[i + 1] ?? '');
  }
  return out;
}

/**
 * @param {string} input
 * @returns {string}
 */
/**
 * Split any HTML that sits before a row's first pipe onto its own line.
 *
 * Stems arrive as one HTML blob using <br> for line breaks, so the first line
 * of a pipe table can read `<p>Intro</p><p>| Vertex | x | y |`. That is a valid
 * pipe row, and its first cell used to become the escaped text `&lt;p&gt;Intro`
 * shown to the student inside the table header.
 *
 * Rejoining the split lines with an empty separator reproduces the input
 * exactly, and the remainder is a pipe row wherever the whole line was, so no
 * table is detected that would not have been detected before.
 */
function splitHtmlPrefixes(lines, separators) {
  const outLines = [];
  const outSeps = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const firstPipe = line.indexOf('|');
    const prefix = firstPipe > 0 ? line.slice(0, firstPipe) : '';
    const lastClose = /<[a-zA-Z/][^>]*>/.test(prefix) ? prefix.lastIndexOf('>') : -1;
    if (lastClose >= 0) {
      outLines.push(line.slice(0, lastClose + 1));
      outSeps.push('');
      outLines.push(line.slice(lastClose + 1));
    } else {
      outLines.push(line);
    }
    if (i < separators.length) outSeps.push(separators[i]);
  }
  return { lines: outLines, separators: outSeps };
}

export function markdownTablesToHtml(input) {
  if (!input || typeof input !== 'string') return input || '';
  if (!input.includes('|')) return input;
  // Already-HTML tables: leave content alone (pipes inside cells are rare; skip
  // whole string conversion when a real table is present and no GFM separator).
  if (/<table[\s>]/i.test(input) && !/\|[\t ]*:?-{1,}:?[\t ]*\|/.test(input)) {
    return input;
  }

  const raw = splitLines(input);
  const { lines, separators } = splitHtmlPrefixes(raw.lines, raw.separators);
  const outLines = [];
  const outSeps = [];
  let i = 0;

  while (i < lines.length) {
    const header = lines[i];
    const next = lines[i + 1];

    if (
      next !== undefined
      && isPipeRow(header)
      && isSeparatorRow(next)
    ) {
      const body = [];
      let j = i + 2;
      while (j < lines.length && isPipeRow(lines[j]) && !isSeparatorRow(lines[j])) {
        body.push(lines[j]);
        j += 1;
      }

      // Header + separator are enough; body may be empty.
      outLines.push(tableToHtml(header, next, body));

      // Keep the break after the last consumed table line (if more content follows).
      if (j - 1 < separators.length && j < lines.length) {
        outSeps.push(separators[j - 1]);
      }

      i = j;
      continue;
    }

    outLines.push(header);
    if (i < separators.length) {
      outSeps.push(separators[i]);
    }
    i += 1;
  }

  // If we ended on a table with no trailing content, outSeps may be short —
  // joinLines only walks separators.length.
  return joinLines(outLines, outSeps);
}

export default markdownTablesToHtml;
